import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  resolveIdentity,
  transferApplicationOwner,
  getMyRequests,
} from "../../actions";
import {
  getApplicationOwner,
  isLoading,
  getActiveApplication,
} from "../../selectors";
import PlaceholderLineLoader from "../PlaceholderLineLoader";
import PersonSearchModal from "./PersonSearchModal";
import { Button, Space, Typography, Modal, Dropdown } from "antd";
import {
  UserSwitchOutlined,
  UserOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { ContentTable } from "@authzsvc/common-react-components";
import { notification } from "antd";
import { types, successAction, errorAction } from "@authzsvc/api-lib";
import { sidebar_identities } from "../../config";
import { showErrorNotification } from "../../utils/index";

const { Text, Link } = Typography;

const Owner = ({
  ownerId,
  activeApplication,
  loading,
  resolveIdentity,
  transferApplicationOwner,
  getMyRequests,
}) => {
  const [owner, setOwner] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadOwner = useCallback(
    async (id) => {
      if (!id) {
        setOwner(null);
        return;
      }
      const identityResponse = await resolveIdentity(id);
      setOwner(identityResponse?.payload?.data || null);
    },
    [resolveIdentity],
  );

  useEffect(() => {
    loadOwner(ownerId);
  }, [ownerId, loadOwner]);

  // Normally an approval is required, the displayed owner shouldn't change yet
  const assignOwner = async (newOwner) => {
    const resp = await transferApplicationOwner(
      activeApplication.application.id,
      newOwner.id,
    );

    if (resp.type === successAction(types.TRANSFER_APPLICATION_OWNER)) {
      if (resp.payload.data.status === "Pending") {
        notification.warning({
          message: "Approval required by new administrator",
          description: `The new owner has been notified. Once they accept, ownership will transfer. Request: "${resp.payload.data.displayName}"`,
        });
      } else {
        await loadOwner(newOwner.id);
      }
    } else if (resp.type === errorAction(types.TRANSFER_APPLICATION_OWNER)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }

    getMyRequests();
  };

  const portalHref = useMemo(() => {
    if (!owner?.upn) return "#";
    return `${sidebar_identities}/identities/${owner.upn}`;
  }, [owner]);

  const tableColumns = useMemo(
    () => [
      { title: "Field", dataIndex: "field", key: "field", width: 180 },
      {
        title: "Value",
        dataIndex: "value",
        key: "value",
        searchable: true,
        render: (val) =>
          val === undefined || val === null || val === "" ? (
            <Text type="secondary">—</Text>
          ) : (
            val
          ),
      },
    ],
    [],
  );

  const tableData = useMemo(() => {
    if (!owner) return [];
    return [
      { key: "displayName", field: "Display Name", value: owner.displayName },
      {
        key: "upn",
        field: "Login (UPN)",
        value: <Text code>{owner.upn}</Text>,
      },
      { key: "type", field: "Type", value: owner.type },
      {
        key: "cernDepartment",
        field: "CERN Department",
        value: owner.cernDepartment,
      },
      { key: "cernGroup", field: "CERN Group", value: owner.cernGroup },
      { key: "cernSection", field: "CERN Section", value: owner.cernSection },
      {
        key: "description",
        field: "Description",
        value: owner.description,
      },
    ];
  }, [owner]);

  const menuItems = useMemo(
    () => [
      {
        key: "change",
        icon: <ReloadOutlined style={{ color: "#1677ff" }} />,
        label: <span style={{ color: "#1677ff" }}>Change</span>,
        style: { backgroundColor: "#e6f4ff" },
      },
      { type: "divider" },
      { key: "details", icon: <InfoCircleOutlined />, label: "Details" },
      { type: "divider" },
      {
        key: "portal",
        icon: <ExportOutlined />,
        label: (
          <a href={portalHref} target="_blank" rel="noopener noreferrer">
            Users Portal
          </a>
        ),
      },
    ],
    [portalHref],
  );

  if (loading) return <PlaceholderLineLoader />;

  const primaryLabel =
    owner?.displayName && owner?.upn
      ? `${owner.displayName} (${owner.upn})`
      : "Assign Owner";

  return (
    <>
      <div>
        {!owner ? (
          // No owner assigned: open picker directly
          <Button
            id="ownerButton"
            color="gray"
            variant="outlined"
            icon={<UserSwitchOutlined />}
            onClick={() => setPickerOpen(true)}
          >
            Set owner
          </Button>
        ) : (
          // Owner exists: dropdown with Change/Details/Portal (Delete not applicable here)
          <>
            {primaryLabel}{" "}
            <Dropdown
              trigger={["click"]}
              placement="bottomLeft"
              menu={{
                items: menuItems,
                onClick: ({ key }) => {
                  if (key === "change") setPickerOpen(true);
                  if (key === "details") setDetailsOpen(true);
                  // "portal" handled by anchor in label
                },
              }}
            >
              <Button
                id="ownerButton"
                color="gray"
                variant="outlined"
                icon={<UserSwitchOutlined />}
              >
                Change owner
              </Button>
            </Dropdown>
          </>
        )}
      </div>

      <PersonSearchModal
        title={!owner ? "Select Owner" : "Change Owner"}
        onSelect={assignOwner}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
      />

      <Modal
        open={detailsOpen}
        onCancel={() => setDetailsOpen(false)}
        onOk={() => setDetailsOpen(false)}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        title={
          <Space>
            <UserOutlined />
            <span>Owner: {owner?.displayName || "Details"}</span>
          </Space>
        }
        centered
        closable
        width={600}
      >
        <ContentTable
          loading={false}
          columns={tableColumns}
          dataSource={tableData}
          pagination={false}
          rowKey="key"
        />
        <Space
          style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}
        >
          <Link target="_blank" rel="noopener noreferrer" href={portalHref}>
            Open in portal <ExportOutlined />
          </Link>
        </Space>
      </Modal>
    </>
  );
};

Owner.propTypes = {
  ownerId: PropTypes.string.isRequired,
  activeApplication: PropTypes.object,
  loading: PropTypes.bool,
  resolveIdentity: PropTypes.func,
  transferApplicationOwner: PropTypes.func,
  getMyRequests: PropTypes.func,
};

export default connect(
  (state) => ({
    activeApplication: getActiveApplication(state),
    owner: getApplicationOwner(state),
    loading: isLoading(state, "resolveIdentity"),
  }),
  { resolveIdentity, transferApplicationOwner, getMyRequests },
)(Owner);
